import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ORIGIN, ApiError, api } from "../../lib/api";
import { convertToWebp } from "../../lib/image";
import type { MediaAsset } from "../../lib/types";

export const assetUrl = (assetId: string): string => `${API_ORIGIN}/media/${assetId}`;

export const useMediaLibrary = () =>
	useQuery({
		queryKey: ["media"],
		queryFn: () => api.get<{ items: MediaAsset[] }>("/api/media"),
	});

export const useUploadMedia = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (file: File): Promise<MediaAsset> => {
			const converted = await convertToWebp(file);

			const form = new FormData();
			form.append("file", converted.blob, converted.filename);
			form.append("width", String(converted.width));
			form.append("height", String(converted.height));

			const response = await fetch(`${API_ORIGIN}/api/media`, {
				method: "POST",
				credentials: "include",
				body: form,
			});

			if (!response.ok) {
				const detail: unknown = await response.json().catch(() => null);
				const message =
					detail && typeof detail === "object" && "message" in detail
						? String(detail.message)
						: `Upload failed with status ${response.status}`;
				throw new ApiError(response.status, message);
			}

			const payload = (await response.json()) as { asset: MediaAsset };
			return payload.asset;
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["media"] }),
	});
};

export const useDeleteMedia = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (assetId: string) => api.remove(`/api/media/${assetId}`),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["media"] }),
	});
};
